import React, { useState, useEffect } from 'react'
import { ChevronDown } from 'lucide-react'
import { SidebarNavItem } from './SidebarNavItem'
import type { SidebarIconName } from '../icons/SidebarIcons'

export interface NavGroupItem {
  label: string
  to: string
  icon: SidebarIconName
  badge?: number // Optional badge count (e.g., hot prospects count)
}

interface SidebarNavGroupProps {
  label: string
  items: NavGroupItem[]
  defaultExpanded?: boolean
  storageKey?: string // For persisting expanded state
}

export const SidebarNavGroup: React.FC<SidebarNavGroupProps> = ({
  label,
  items,
  defaultExpanded = true,
  storageKey,
}) => {
  // Initialize expanded state from localStorage or default
  const [isExpanded, setIsExpanded] = useState(() => {
    if (storageKey) {
      const stored = localStorage.getItem(`sidebar-group-${storageKey}`)
      if (stored !== null) {
        return stored === 'true'
      }
    }
    return defaultExpanded
  })

  // Persist expanded state to localStorage
  useEffect(() => {
    if (storageKey) {
      localStorage.setItem(`sidebar-group-${storageKey}`, String(isExpanded))
    }
  }, [isExpanded, storageKey])

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <div className="mb-1">
      {/* Group header */}
      <button
        onClick={toggleExpanded}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors rounded-md hover:bg-[var(--bg-hover)]"
        style={{ color: 'var(--text-muted)' }}
        aria-expanded={isExpanded}
        aria-controls={`nav-group-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <span>{label}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
          style={{ color: 'var(--text-subtle)' }}
        />
      </button>

      {/* Group items */}
      <div
        id={`nav-group-${label.toLowerCase().replace(/\s+/g, '-')}`}
        className={`overflow-hidden transition-all duration-200 ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="pl-1 pt-1 space-y-0.5">
          {items.map((item) => (
            <SidebarNavItem
              key={item.to}
              to={item.to}
              label={item.label}
              icon={item.icon}
              badge={item.badge}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default SidebarNavGroup
